/**
 * SystemDiagnostics.jsx — Iter-363 (P0 SMS INCIDENT)
 *
 * Super-admin-only page rendering the production environment status
 * and Twilio/SendGrid health WITHOUT the operator needing to extract
 * a JWT or curl anything. Every gate the send_sms pipeline checks is
 * visible here with a live "Send Test SMS" action + last successful /
 * failed delivery details.
 *
 * Route: /admin/system/diagnostics  (super_admin only)
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  ArrowLeft, Loader2, ShieldCheck, ShieldAlert, RefreshCw, Send,
} from 'lucide-react';
import { toast } from 'sonner';

const VERDICT_STYLE = {
  healthy: { cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', Icon: ShieldCheck, label: 'HEALTHY' },
  was_recently_blocked: { cls: 'bg-amber-100 text-amber-800 border-amber-300', Icon: ShieldAlert, label: 'RECENTLY BLOCKED' },
  blocked_kill_switch: { cls: 'bg-rose-100 text-rose-800 border-rose-300', Icon: ShieldAlert, label: 'BLOCKED · KILL SWITCH' },
  blocked_sandbox: { cls: 'bg-rose-100 text-rose-800 border-rose-300', Icon: ShieldAlert, label: 'BLOCKED · SANDBOX' },
  blocked_comm_guard: { cls: 'bg-rose-100 text-rose-800 border-rose-300', Icon: ShieldAlert, label: 'BLOCKED · COMM GUARD' },
  config_incomplete: { cls: 'bg-rose-100 text-rose-800 border-rose-300', Icon: ShieldAlert, label: 'CONFIG INCOMPLETE' },
};

function KV({ label, value, ok, warn, mono }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-slate-100 py-1.5 text-sm">
      <span className="text-slate-600">{label}</span>
      <span className={`font-semibold text-right ${mono ? 'font-mono text-xs' : ''} ${
        ok === true ? 'text-emerald-700' : ok === false ? 'text-rose-700' : warn ? 'text-amber-700' : 'text-slate-900'
      }`}>
        {value ?? '—'}
      </span>
    </div>
  );
}

export default function SystemDiagnostics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [err, setErr] = useState(null);
  const [buildInfo, setBuildInfo] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [deliveryStatus, setDeliveryStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const [pipelineTest, setPipelineTest] = useState(null);
  const [pipelineTesting, setPipelineTesting] = useState(false);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const [diag, pipe, bi] = await Promise.all([
        api.get('/admin/system/sms-pipeline-diagnostic'),
        api.get('/admin/system/notification-pipeline-diagnostic'),
        api.get('/build-info'),
      ]);
      setData(diag.data);
      setPipeline(pipe.data);
      setBuildInfo(bi.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load diagnostics — super_admin required.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const runPipelineTest = async () => {
    if (!window.confirm('This will send a REAL email + SMS to YOUR admin account. Continue?')) return;
    setPipelineTesting(true); setPipelineTest(null);
    try {
      const res = await api.post('/admin/system/notification-pipeline-test', {
        confirm: true, channels: ['in_app', 'email', 'sms'],
      });
      setPipelineTest(res.data);
      if (res.data?.all_ok) toast.success('All three channels reported success. Check your email/SMS.');
      else toast.error('At least one channel failed — see per-channel detail below.');
    } catch (e) {
      setPipelineTest({ error: e?.response?.data?.detail || 'Pipeline test crashed' });
      toast.error('Pipeline test failed');
    } finally {
      setPipelineTesting(false);
    }
  };

  const sendTest = async () => {
    if (!testPhone.trim()) { toast.error('Enter a phone number (E.164) or leave blank to use your account phone.'); return; }
    setSending(true); setTestResult(null); setDeliveryStatus(null);
    try {
      const res = await api.post('/admin/system/comm-health/test-sms', { to_phone: testPhone.trim() });
      setTestResult(res.data);
      const sid = res.data?.provider_response?.message_sid;
      const st = res.data?.provider_response?.status;
      if (st === 'sent' || st === 'queued') toast.success(`Twilio accepted: SID ${sid}`);
      else toast.error(`Provider status: ${st}. See details below.`);

      // Iter-364 — Poll delivery status for up to 30s so the operator
      // sees the SID transition queued → sent → delivered (or failed).
      if (sid) {
        setDeliveryStatus({ message_sid: sid, status: st || 'queued', polling: true });
        const started = Date.now();
        const poll = async () => {
          try {
            const s = await api.get(`/admin/system/sms-test-status/${sid}`);
            setDeliveryStatus({ ...s.data, polling: !s.data.final });
            if (s.data.final) {
              if (s.data.delivered) toast.success(`Delivered — SID ${sid}`);
              else toast.error(`${s.data.status}: ${s.data.error_message || 'see Twilio console'}`);
              return;
            }
          } catch (_) { /* keep polling */ }
          if (Date.now() - started < 30000) setTimeout(poll, 2500);
          else setDeliveryStatus((prev) => prev ? { ...prev, polling: false, timed_out: true } : prev);
        };
        setTimeout(poll, 2500);
      }

      await load();
    } catch (e) {
      setTestResult({ error: e?.response?.data?.detail || 'Test send failed' });
      toast.error('Test send failed — see details below.');
    } finally {
      setSending(false);
    }
  };

  const verdict = data?.verdict || 'healthy';
  const vs = VERDICT_STYLE[verdict] || VERDICT_STYLE.healthy;
  const VIcon = vs.Icon;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="system-diagnostics-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-slate-900">System Diagnostics</h1>
            <p className="text-xs text-slate-500">Live SMS/Email pipeline health · super_admin only</p>
          </div>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {err && (
          <Card className="border-rose-200 bg-rose-50">
            <CardContent className="p-4 text-rose-700" data-testid="diag-error">{err}</CardContent>
          </Card>
        )}

        {data && (
          <>
            {/* VERDICT — the one-line answer */}
            <Card className={`border-2 ${vs.cls}`} data-testid="diag-verdict-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <VIcon className="w-8 h-8" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] uppercase tracking-wide font-bold" data-testid="diag-verdict-label">Verdict</p>
                    <p className="text-lg font-bold" data-testid="diag-verdict-value">{vs.label}</p>
                  </div>
                </div>
                <ul className="mt-3 space-y-1.5 text-sm list-disc list-inside" data-testid="diag-reasons">
                  {(data.reasons || []).map((r, i) => (<li key={i}>{r}</li>))}
                </ul>
              </CardContent>
            </Card>

            {/* NOTIFICATION PIPELINE — Iter-365 */}
            {pipeline && (
              <Card
                className={`border-2 ${pipeline.verdict === 'all_healthy'
                  ? 'border-emerald-300 bg-emerald-50'
                  : 'border-rose-300 bg-rose-50'}`}
                data-testid="pipeline-diag-card"
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>Notification Pipeline (email · sms · in-app)</span>
                    <Badge
                      className={pipeline.verdict === 'all_healthy'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-rose-600 text-white'}
                      data-testid="pipeline-verdict"
                    >
                      {pipeline.verdict === 'all_healthy' ? 'ALL HEALTHY' : 'DEGRADED'}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {['email', 'sms', 'in_app'].map((ch) => {
                    const c = pipeline.channels?.[ch] || {};
                    return (
                      <div key={ch} className="border-t border-slate-200 pt-2" data-testid={`pipeline-ch-${ch}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={c.healthy
                            ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                            : 'bg-rose-100 text-rose-800 border-rose-300'}>
                            {c.healthy ? 'OK' : 'BLOCKED'}
                          </Badge>
                          <span className="font-semibold uppercase text-xs tracking-wide">{ch}</span>
                        </div>
                        {(c.reasons || []).length > 0 && (
                          <ul className="text-xs list-disc list-inside text-rose-800 space-y-1">
                            {(c.reasons || []).map((r, i) => <li key={i}>{r}</li>)}
                          </ul>
                        )}
                      </div>
                    );
                  })}

                  {/* Automation flag (platform_settings) */}
                  <div className="border-t border-slate-200 pt-2" data-testid="pipeline-automation">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={pipeline.automation?.healthy
                        ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                        : 'bg-amber-100 text-amber-800 border-amber-300'}>
                        {pipeline.automation?.healthy ? 'OK' : 'DISABLED'}
                      </Badge>
                      <span className="font-semibold uppercase text-xs tracking-wide">Scheduled reminders auto-send</span>
                    </div>
                    {(pipeline.automation?.reasons || []).length > 0 && (
                      <ul className="text-xs list-disc list-inside text-amber-900 space-y-1">
                        {(pipeline.automation?.reasons || []).map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                    )}
                  </div>

                  {/* 24h counters */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2 border-t border-slate-200">
                    {Object.entries(pipeline.counters_24h || {}).map(([k, v]) => (
                      <div key={k} className="rounded border border-slate-200 bg-white p-2" data-testid={`pipeline-counter-${k}`}>
                        <p className="text-[10px] uppercase text-slate-500 leading-tight">{k.replace(/_/g, ' ')}</p>
                        <p className={`text-lg font-bold ${v > 0 ? 'text-slate-900' : 'text-slate-400'}`}>{v}</p>
                      </div>
                    ))}
                  </div>

                  {/* Full-pipeline self-test */}
                  <div className="pt-2 border-t border-slate-200">
                    <Button
                      onClick={runPipelineTest}
                      disabled={pipelineTesting}
                      variant="outline"
                      data-testid="pipeline-selftest-btn"
                    >
                      {pipelineTesting
                        ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Firing all channels…</>
                        : <><Send className="w-4 h-4 mr-2" />Fire full-pipeline self-test to my admin account</>}
                    </Button>
                    <p className="text-[11px] text-slate-500 mt-1">
                      Sends a real email + SMS to YOUR admin email/phone (from the users table),
                      plus writes an in-app notification. Uses <code>force=true</code> to bypass
                      comm_guard for this one dispatch — production sends are unaffected.
                    </p>
                    {pipelineTest && (
                      <pre className="mt-2 rounded bg-slate-900 text-slate-100 p-3 text-[11px] overflow-x-auto" data-testid="pipeline-selftest-result">
                        {JSON.stringify(pipelineTest, null, 2)}
                      </pre>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ENVIRONMENT — the exact values on this pod */}
            <Card>
              <CardHeader><CardTitle className="text-base">Environment Classification</CardTitle></CardHeader>
              <CardContent>
                <KV label="APP_ENV" value={data.comm_guard?.app_env_raw || data.comm_guard?.app_env || '(not set)'} ok={data.comm_guard?.is_production} mono />
                <KV label="Recognised as production" value={String(data.comm_guard?.is_production)} ok={data.comm_guard?.is_production} />
                <KV label="Reason" value={data.comm_guard?.reason} mono />
                <KV label="PUBLIC_APP_URL" value={data.comm_guard?.public_app_url || '(not set)'} mono />
                <KV label="Detected on prod host" value={String(data.comm_guard?.on_prod_host)} ok={data.comm_guard?.on_prod_host} />
                <KV label="Build commit" value={buildInfo?.commit} mono />
                <KV label="Iteration marker" value={buildInfo?.iteration_marker} mono />
                {!data.comm_guard?.is_production && (
                  <div className="mt-3 rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800" data-testid="diag-appenv-fix">
                    <p className="font-bold mb-1">Fix: set the production pod env var</p>
                    <code className="block bg-white/70 p-2 rounded font-mono text-xs">APP_ENV=production</code>
                    <p className="mt-2 text-xs">
                      This is a pod-level env var. If you cannot set it via the Emergent Deploy dashboard,
                      open a support ticket referencing this diagnostic page. Redeploy after saving.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* KILL SWITCHES */}
            <Card>
              <CardHeader><CardTitle className="text-base">Kill Switches</CardTitle></CardHeader>
              <CardContent>
                <KV label="SMS_DELIVERY_PAUSED" value={String(data.kill_switches?.SMS_DELIVERY_PAUSED)} ok={!data.kill_switches?.SMS_DELIVERY_PAUSED} />
                <KV label="TWILIO_SANDBOX_MODE" value={String(data.twilio?.sandbox_mode)} ok={!data.twilio?.sandbox_mode} />
              </CardContent>
            </Card>

            {/* PROVIDER */}
            <Card>
              <CardHeader><CardTitle className="text-base">Twilio Provider</CardTitle></CardHeader>
              <CardContent>
                <KV label="Account SID prefix" value={data.twilio?.account_sid_prefix} ok={!!data.twilio?.account_sid_prefix} mono />
                <KV label="Auth token configured" value={String(data.twilio?.auth_token_configured)} ok={data.twilio?.auth_token_configured} />
                <KV label="From number (last 4)" value={data.twilio?.from_number_last4 ? `+1***${data.twilio.from_number_last4}` : '(none)'} ok={!!data.twilio?.from_number_last4} mono />
                <KV label="Messaging Service SID" value={String(data.twilio?.messaging_service_sid_configured)} />
                <KV label="Client initialises" value={String(data.twilio?.client_initialises)} ok={data.twilio?.client_initialises} />
                {data.twilio?.client_error && <KV label="Client error" value={data.twilio.client_error} ok={false} mono />}
              </CardContent>
            </Card>

            {/* COUNTERS */}
            <Card>
              <CardHeader><CardTitle className="text-base">Last 24 Hours</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {Object.entries(data.counters_24h || {}).map(([k, v]) => (
                  <div key={k} className="rounded border border-slate-200 p-2" data-testid={`counter-${k}`}>
                    <p className="text-[10px] uppercase text-slate-500">{k.replace(/_/g, ' ')}</p>
                    <p className={`text-xl font-bold ${
                      k.includes('blocked') || k.includes('paused') || k.includes('failed') || k.includes('undelivered')
                        ? (v > 0 ? 'text-rose-700' : 'text-slate-400')
                        : (v > 0 ? 'text-emerald-700' : 'text-slate-400')
                    }`}>{v}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* LAST EVENTS */}
            <Card>
              <CardHeader><CardTitle className="text-base">Last Deliveries</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                {['success', 'delivered', 'failure', 'blocked_non_prod'].map((k) => {
                  const l = data.last?.[k];
                  if (!l) return <KV key={k} label={k.replace(/_/g, ' ')} value="none in recent window" />;
                  const ts = new Date(l.at).toLocaleString('en-US', {
                    timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit',
                    month: '2-digit', day: '2-digit', year: 'numeric', timeZoneName: 'short',
                  });
                  return (
                    <KV
                      key={k}
                      label={k.replace(/_/g, ' ')}
                      value={`${ts} · ***${l.phone_last4}${l.error_message ? ` · ${l.error_message}` : ''}`}
                      mono
                    />
                  );
                })}
              </CardContent>
            </Card>

            {/* TEST SEND */}
            <Card>
              <CardHeader><CardTitle className="text-base">Send Test SMS</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    placeholder="+15551234567 (E.164)"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    data-testid="test-sms-phone"
                  />
                  <Button onClick={sendTest} disabled={sending} data-testid="test-sms-send">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Send
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Sends a health-check SMS through the production pipeline (force=true bypasses eligibility checks).
                  A successful send returns a Twilio Message SID visible below and in your Twilio console.
                </p>
                {testResult && (
                  <pre className="rounded bg-slate-900 text-slate-100 p-3 text-[11px] overflow-x-auto" data-testid="test-sms-result">
                    {JSON.stringify(testResult, null, 2)}
                  </pre>
                )}
                {deliveryStatus && (
                  <div
                    className={`rounded border p-3 text-sm flex items-start gap-3 ${
                      deliveryStatus.delivered
                        ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
                        : deliveryStatus.final
                          ? 'border-rose-300 bg-rose-50 text-rose-900'
                          : 'border-sky-300 bg-sky-50 text-sky-900'
                    }`}
                    data-testid="test-sms-delivery-status"
                  >
                    {deliveryStatus.polling && <Loader2 className="w-4 h-4 animate-spin mt-0.5" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-bold uppercase text-xs tracking-wide">
                        Delivery status:{' '}
                        <span data-testid="test-sms-delivery-status-value">
                          {deliveryStatus.status || 'unknown'}
                        </span>
                      </p>
                      <p className="mt-1 font-mono text-[11px] break-all">
                        SID {deliveryStatus.message_sid}
                        {deliveryStatus.phone_last4 ? ` → ***${deliveryStatus.phone_last4}` : ''}
                      </p>
                      {deliveryStatus.error_message && (
                        <p className="mt-1 text-xs">Twilio error: {deliveryStatus.error_message} (code {deliveryStatus.error_code || '?'})</p>
                      )}
                      {deliveryStatus.timed_out && (
                        <p className="mt-1 text-xs italic">No final delivery event within 30s — check Twilio console or webhook receipt.</p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* P0-2026-09 — Calendar/Invite/Ops production tracing */}
            <P0_2026_09_Panel />
          </>
        )}
      </main>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// P0-2026-09 · Calendar visibility + Ops routing production traces
// ─────────────────────────────────────────────────────────────────────
function P0_2026_09_Panel() {
  const [shiftId, setShiftId] = useState('');
  const [visLoading, setVisLoading] = useState(false);
  const [visData, setVisData] = useState(null);
  const [routingLoading, setRoutingLoading] = useState(false);
  const [routingData, setRoutingData] = useState(null);
  const [notifyChannel, setNotifyChannel] = useState('new_client_event');
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifyResult, setNotifyResult] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const traceVisibility = async () => {
    if (!shiftId.trim()) { toast.error('Paste a shift UUID'); return; }
    setVisLoading(true); setVisData(null);
    try {
      const res = await api.get(`/admin/diag/shift-visibility/${shiftId.trim()}`);
      setVisData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load shift trace');
    } finally { setVisLoading(false); }
  };

  const restoreShift = async () => {
    if (!shiftId.trim()) return;
    if (!window.confirm('Flip is_test / is_test_data / is_archived to FALSE for this shift?')) return;
    setRestoring(true);
    try {
      const res = await api.post(`/admin/diag/restore-shift/${shiftId.trim()}`);
      toast.success('Shift restored — re-run visibility trace to confirm');
      setVisData(null);
      // Auto re-trace
      const r2 = await api.get(`/admin/diag/shift-visibility/${shiftId.trim()}`);
      setVisData(r2.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed');
    } finally { setRestoring(false); }
  };

  const loadRouting = async () => {
    setRoutingLoading(true); setRoutingData(null);
    try {
      const res = await api.get('/admin/diag/ops-routing');
      setRoutingData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load ops routing');
    } finally { setRoutingLoading(false); }
  };

  const runNotifyTest = async () => {
    if (!window.confirm(`Send a REAL SMS + email to every '${notifyChannel}' recipient?`)) return;
    setNotifyLoading(true); setNotifyResult(null);
    try {
      const res = await api.post('/admin/diag/ops-notify-test', {
        channel_key: notifyChannel,
      });
      setNotifyResult(res.data);
      toast.success('Fired — check recipient inbox/phone within a minute');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test fan-out failed');
    } finally { setNotifyLoading(false); }
  };

  return (
    <Card className="border-2 border-cyan-200" data-testid="p0-2026-09-panel">
      <CardHeader className="bg-gradient-to-r from-cyan-50 to-white">
        <CardTitle className="text-base">P0-2026-09 · Calendar, Invites & Ops routing traces</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6 pt-4">
        {/* Shift visibility trace */}
        <div className="space-y-2">
          <label className="text-sm font-semibold">Shift visibility trace</label>
          <div className="flex gap-2">
            <Input
              placeholder="shift UUID (e.g. from /admin/shifts/…)"
              value={shiftId}
              onChange={(e) => setShiftId(e.target.value)}
              className="font-mono text-xs"
              data-testid="p0-shift-id-input"
            />
            <Button onClick={traceVisibility} disabled={visLoading} data-testid="p0-trace-btn">
              {visLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Trace'}
            </Button>
            {visData && (visData.flags?.is_test || visData.flags?.is_test_data || visData.flags?.is_archived) && (
              <Button variant="destructive" onClick={restoreShift} disabled={restoring} data-testid="p0-restore-btn">
                {restoring ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Restore'}
              </Button>
            )}
          </div>
          {visData && (
            <div className="rounded border p-3 bg-slate-50 text-xs space-y-1">
              <div className={`inline-block px-2 py-0.5 rounded font-bold ${visData.calendar_default_query_would_return ? 'bg-emerald-200 text-emerald-900' : 'bg-rose-200 text-rose-900'}`}>
                {visData.calendar_default_query_would_return ? 'CALENDAR: VISIBLE' : 'CALENDAR: HIDDEN'}
              </div>
              {visData.excluded_reasons?.length > 0 && (
                <ul className="text-rose-800 list-disc pl-4">
                  {visData.excluded_reasons.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              )}
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 pt-2">
                <div><b>Venue:</b> {visData.venue_name || '—'}</div>
                <div><b>Title:</b> {visData.event_title || '—'}</div>
                <div><b>Date:</b> {visData.shift_date}</div>
                <div><b>Time TBD:</b> {visData.is_time_tbd ? 'YES' : 'no'}</div>
                <div><b>Client:</b> {visData.client_company || visData.client_email}</div>
                <div><b>Client is_test:</b> {String(visData.client_is_test)}</div>
                <div><b>City/State:</b> {visData.city}, {visData.state}</div>
                <div><b>Status:</b> {visData.status}</div>
              </div>
              {visData.audit_trail?.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer font-semibold">Audit trail ({visData.audit_trail.length})</summary>
                  <pre className="mt-1 bg-slate-900 text-slate-100 p-2 rounded overflow-x-auto">
                    {JSON.stringify(visData.audit_trail, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          )}
        </div>

        {/* Ops routing dump */}
        <div className="space-y-2 border-t pt-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-semibold">Ops-notification routing</label>
            <Button size="sm" onClick={loadRouting} disabled={routingLoading} data-testid="p0-routing-load-btn">
              {routingLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Load'}
            </Button>
          </div>
          {routingData && (
            <div className="rounded border p-3 bg-slate-50 text-xs space-y-2">
              {routingData.missing_channels?.length > 0 && (
                <div className="bg-rose-100 text-rose-900 p-2 rounded">
                  Missing channels: {routingData.missing_channels.join(', ')}
                </div>
              )}
              {Object.entries(routingData.channels || {}).map(([ch, rows]) => (
                <details key={ch} open>
                  <summary className="cursor-pointer font-semibold">{ch} ({rows.length})</summary>
                  <table className="w-full mt-1 text-[11px]">
                    <thead><tr className="text-left"><th>Name</th><th>Phone</th><th>Email</th><th>Active</th></tr></thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.id} className="border-t">
                          <td>{r.display_name}</td>
                          <td className="font-mono">{r.resolved_phone || '—'}</td>
                          <td className="font-mono">{r.resolved_email || '—'}</td>
                          <td>{r.active ? '✓' : '✗'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              ))}
            </div>
          )}
        </div>

        {/* Fire real ops-notify test */}
        <div className="space-y-2 border-t pt-4">
          <label className="text-sm font-semibold">End-to-end ops fan-out test</label>
          <div className="flex gap-2">
            <select
              value={notifyChannel}
              onChange={(e) => setNotifyChannel(e.target.value)}
              className="border rounded px-2 text-sm"
              data-testid="p0-notify-channel"
            >
              <option value="onsite_message">onsite_message</option>
              <option value="new_client_event">new_client_event</option>
              <option value="client_event_change">client_event_change</option>
            </select>
            <Button onClick={runNotifyTest} disabled={notifyLoading} data-testid="p0-notify-btn">
              {notifyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Fire real SMS + email'}
            </Button>
          </div>
          {notifyResult && (
            <pre className="rounded bg-slate-900 text-slate-100 p-2 text-[11px] overflow-x-auto">
              {JSON.stringify(notifyResult, null, 2)}
            </pre>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
